import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { useAssetLookup } from "../../api/useAssetLookup";
import { FindingFixes } from "../../findings/FindingFixes";
import { FixTip } from "../../findings/FixTip";
import { formatGameTime } from "../../format/formatGameTime";
import { formatList } from "../../format/formatList";
import type { Finding } from "../../model/Finding";
import { FIX_TIPS } from "../../model/FIX_TIPS";
import { findingsOf } from "../../model/findingsOf";
import { SEVERITY_LABELS } from "../../model/SEVERITY_LABELS";
import { AssetIcon } from "../AssetIcon";

const INTRO = msg({ id: "analysis.fights.intro", message: "Escaped you with low HP:" });
const INTRO_AFTER_LANE = msg({
  id: "analysis.fights.intro_after_lane",
  message:
    "After the lane phase enemies escaped you with low HP {count, plural, one {once} other {# times}}:",
});
const TIMES = msg({ id: "analysis.fights.times", message: "at {times}" });
const ITEMS = msg({ id: "analysis.fights.items", message: "had {items}" });

type EscapeCardsProps = {
  findings: readonly Finding[];
};

// Находки `enemy_escaped` карточками: герой, сколько раз ушёл, когда и с какими предметами побега;
// под карточками — общий совет. Разборы с важностью считают побеги только после фазы линий —
// так и подписано; у старых разборов ещё могут быть советы против героя.
export const EscapeCards = ({ findings }: EscapeCardsProps) => {
  const { _, i18n } = useLingui();
  const findHero = useAssetLookup("heroes");
  const findItem = useAssetLookup("items");
  const escapes = findingsOf(findings, "enemy_escaped");
  if (escapes.length === 0) {
    return null;
  }
  return (
    <>
      <p className="text-sm text-foreground-soft">
        {escapes.some((finding) => finding.impact > 0)
          ? _({
              ...INTRO_AFTER_LANE,
              values: { count: escapes.reduce((sum, f) => sum + f.params.count, 0) },
            })
          : _(INTRO)}
      </p>
      <ul className="flex flex-wrap gap-3">
        {escapes.map((finding) => {
          const hero = findHero(finding.params.hero_id);
          const items = finding.params.escape_item_ids.map((id) => findItem(id).name);
          return (
            <li
              key={finding.params.hero_id}
              data-finding={finding.code}
              data-severity={finding.severity}
              className="flex min-w-[220px] items-center gap-2.5 rounded-[10px] bg-surface-raised px-3.5 py-2.5"
            >
              <AssetIcon asset={hero} size={48} decorative className="rounded-[10px]" />
              <div className="flex flex-col">
                <span className="font-semibold">
                  <span className="sr-only">{_(SEVERITY_LABELS[finding.severity])}: </span>
                  {hero.name} <span className="font-mono text-warn">{finding.params.count}×</span>
                </span>
                <span className="text-xs text-muted">
                  {_({
                    ...TIMES,
                    values: {
                      times: formatList(finding.params.times_s.map(formatGameTime), i18n.locale),
                    },
                  })}
                </span>
                {items.length > 0 && (
                  <span className="text-xs text-muted">
                    {_({ ...ITEMS, values: { items: formatList(items, i18n.locale) } })}
                  </span>
                )}
                <FindingFixes
                  finding={finding}
                  dataOnly
                  className="mt-1 flex max-w-md flex-col gap-1"
                />
              </div>
            </li>
          );
        })}
      </ul>
      {FIX_TIPS.enemy_escaped !== undefined && <FixTip tip={FIX_TIPS.enemy_escaped} />}
    </>
  );
};
